import { BridgeElement } from "@hotwired/hotwire-native-bridge"

// Builds the native menu entries for the `menu` and `title-menu` bridge
// components from their hidden item elements. Each item carries:
//   data-bridge-title        entry label
//   data-bridge-ios-image    SF Symbol for the entry (Android maps it to a drawable)
//   data-bridge-action       "share" for a native share entry, otherwise omitted
//   data-bridge-url          share URL (used when action is "share")
//   data-bridge-destructive  "true" to style the entry as destructive (red)
//   data-bridge-checked      "true" to show the entry as the current choice
//   data-bridge-group        integer section; a divider separates sections
//
// Optional keys are left out when absent so older native clients decode the
// same payload they always have.
export function bridgeMenuItems(itemTargets) {
  return itemTargets.map((item, index) => {
    const bridgeItem = new BridgeElement(item)
    const entry = {
      index,
      title: bridgeItem.title,
      // Native clients decode `image` as a non-null string; an entry without
      // an icon (e.g. a routine name) must send "" rather than null.
      image: bridgeItem.bridgeAttribute("ios-image") ?? "",
      action: bridgeItem.bridgeAttribute("action"),
      url: bridgeItem.bridgeAttribute("url"),
    }

    if (bridgeItem.bridgeAttribute("destructive") === "true") {
      entry.destructive = true
    }
    if (bridgeItem.bridgeAttribute("checked") === "true") {
      entry.checked = true
    }

    const group = bridgeItem.bridgeAttribute("group")
    if (group !== null && group !== undefined && group !== "") {
      entry.group = Number.parseInt(group, 10)
    }

    return entry
  })
}

// Native replies with `{ index }` when a non-share entry is tapped; click the
// matching hidden element so links and forms behave exactly as on the web.
export function clickBridgeMenuItem(itemTargets, message) {
  const item = itemTargets[message?.data?.index]

  if (item) {
    new BridgeElement(item).click()
  }
}

// A morph refresh can change a menu's items without reconnecting its
// controller, so re-send "connect" (`resend`) whenever anything inside the
// controller's element changes. A burst of mutations sends once. Sets the
// controller's `observer` and `resendTimer`.
export function observeBridgeMenuChanges(controller, resend) {
  controller.isConnected = true
  controller.observer = new MutationObserver(() => {
    if (!controller.isConnected) return

    clearTimeout(controller.resendTimer)
    controller.resendTimer = setTimeout(resend, 0)
  })
  controller.observer.observe(controller.element, { subtree: true, childList: true, attributes: true, characterData: true })
}

// Stops observeBridgeMenuChanges and drops a resend still pending.
export function stopObservingBridgeMenuChanges(controller) {
  controller.isConnected = false
  controller.observer?.disconnect()
  clearTimeout(controller.resendTimer)
}
